// L'étude imprimable (CDC-06) — la page que Chromium headless capture en PDF.
// Standalone (pas de shell) : page de garde → sommaire → les thématiques servies → méthodologie.
// Auth : ?pdf_token=<jeton 5 min> minté par l'endpoint etude.pdf (droits réels du porteur).
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import Logo from '../Logo'
import { BandeauPerimetre, ChartCard, KpiCards, LlmBlock, NoteMethodeEchelles } from './components'
import { DOMAINE_PUBLIC, NOM_PRODUIT } from '../constants'
import { API, type SynthesePayload, type ThemePayload } from './types'
import { chargerStructure } from '../structure'
import '@fontsource/ibm-plex-serif/400.css'
import '@fontsource/ibm-plex-serif/600.css'
import '@fontsource/ibm-plex-sans/400.css'
import './obs.css'
import './print.css'
import { IMPRESSION_TOUS_EQUIPEMENTS, SECTION_TOUS_EQUIPEMENTS } from './tousEquipements'
import { sansMasques } from './masques'
import { normaliserPrefs } from './prefs'
import { populationCouverture } from './population'

// T4 (27/08) : la liste des thématiques vient du serveur (`/meta`, registre THEMES — même
// source que la nav et le sommaire), plus jamais d'une liste figée ici : une 12e thématique
// derrière un drapeau sortirait sinon du PDF sans erreur visible (repérage du 27/08).
// Repli figé seulement si `/meta` échoue — le document reste alors celui d'avant.
// 15/09 : `rev` ajouté ici (13ᵉ thématique « Revenus »). Le repli ne sert QUE si `/meta`
// échoue — mais l'oublier ferait sortir du PDF, en mode dégradé, la thématique qui porte
// désormais le niveau de vie : l'élu recevrait un document sans revenus SANS erreur.
// A20 (08/10) : avant ce repli figé, la structure publique (`/api/observatoire/structure`,
// même registre, drapeau ARBORESCENCE_V2 compris) ; le repli figé est la liste v1 complète
// (13, `equ` compris : THEME_EQU=1 en prod) — le dernier filet, pas la source.
const THEME_IDS_REPLI = ['dem', 'rev', 'log', 'fin', 'soc', 'sec', 'san', 'ene', 'eco', 'mob', 'edu', 'env', 'equ']

export default function PrintView() {
  const { insee } = useParams<{ insee: string }>()
  const [params] = useSearchParams()
  const token = params.get('pdf_token')
  // Van V9 : ?sections=dem,log → n'assembler QUE ces thématiques (défaut : les 9).
  // La validation forte est backend (ids ⊆ thèmes) ; ici simple filtre défensif.
  const sectionsParam = params.get('sections')
  const restreindre = (ids: string[]) => {
    const choisis = sectionsParam ? ids.filter(t => sectionsParam.split(',').includes(t)) : ids
    return choisis.length > 0 ? choisis : ids
  }
  const [syn, setSyn] = useState<SynthesePayload | null>(null)
  const [themes, setThemes] = useState<ThemePayload[]>([])
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!insee || !token) return
    let alive = true
    const H = { headers: { Authorization: `Bearer ${token}` } }
    const run = async () => {
      try {
        // la liste des thématiques servies (registre + drapeaux), puis tout le reste en
        // parallèle : le virtual-time de Chromium se fige sur les XHR en vol — deux round-trips
        let ids = THEME_IDS_REPLI
        try {
          const m = await axios.get<{ themes?: { id: string }[] }>(`${API}/observatoire/${insee}/meta`, H)
          if (m.data?.themes?.length) ids = m.data.themes.map(t => t.id)
        } catch {
          // A20 : la structure publique d'abord, le repli figé ensuite.
          const s = await chargerStructure()
          if (s?.themes.length) ids = s.themes.map(t => t.id)
        }
        const idsEffectifs = restreindre(ids)
        // P12 : ce que le compte qui imprime a masqué sur cette commune — dans le MÊME aller
        // parallèle (pas de troisième round-trip sous le temps virtuel de Chromium). Un échec
        // n'annule pas l'étude : elle sort alors complète, jamais tronquée par erreur.
        const masquesP = axios.get<{ masques?: unknown }>(
          `${API}/observatoire/${insee}/impression/masques`, H)
          .then(r => normaliserPrefs({ masques: r.data?.masques }).masques)
          .catch(() => [])
        const [masques, s, ...trs] = await Promise.all([
          masquesP,
          axios.get<SynthesePayload>(`${API}/observatoire/${insee}/synthese`, H),
          ...idsEffectifs.map(tid => axios.get<ThemePayload>(`${API}/observatoire/${insee}/theme/${tid}`, H)),
        ])
        if (!alive) return
        setSyn(s.data)
        setThemes(trs.map(r => ({ ...r.data,
          sections: sansMasques(r.data.sections, r.data.theme.id, masques) })))
        setReady(true)
      } catch {
        // fail-closed (codex) : un seul appel en échec = document d'erreur MINIMAL
        // (1 page) que la garde backend (comptage de pages) rejette → 503, jamais
        // un PDF « Assemblage… » livré au client.
        if (alive) setFailed(true)
      }
    }
    run()
    return () => { alive = false }
  }, [insee, token])

  if (!insee || !token) return <p>Jeton manquant — ce document se génère depuis le bouton « Télécharger l'étude ».</p>
  if (failed) return <p data-print-failed="1">Échec d'assemblage de l'étude.</p>
  if (!ready || !syn) return <p>Assemblage de l'étude…</p>

  // Ce qui sera réellement IMPRIMÉ. Sommaire et pages doivent dire la même chose,
  // sinon le sommaire annonce des pages qui n'existent pas.
  const themesServis = themes.filter(t => t.sections.length > 0)

  const c = syn.commune
  const populationCommune = populationCouverture(c)
  const date =new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="obs2 obs2-print" data-print-ready="1">
      {/* ── Page de garde ── */}
      <section className="print-cover">
        {/* `animation={false}` : la marque respire depuis le 18/09 (demande Cyrille), mais
            cette page-ci n'est pas regardée, elle est REPRODUITE — par un Chromium headless
            qui en fait le PDF livré au client. index.css neutralise déjà l'animation sous
            `@media print` ; la prop est la seconde garde, celle qui ne dépend pas de la
            façon dont ce Chromium-là applique ses media queries au moment du tirage. Une
            barre figée à 78 % de sa hauteur sur une couverture d'étude serait un défaut
            permanent, et invisible en revue. */}
        <Logo animation={false} />
        <div className="print-cover-kicker">Données officielles</div>
        <h1>{c.nom}</h1>
        <div className="print-cover-meta">
          {/* #665 : la couverture dit quelle population — un PDF n'a pas de survol. */}
          INSEE {c.code}{populationCommune ? ` · ${populationCommune}` : ''}
        </div>
        <div className="print-cover-date">Étude générée le {date} — données officielles certifiées</div>
      </section>

      {/* ── Sommaire ── */}
      <section className="print-page">
        <h2 className="print-h2">Sommaire</h2>
        <ol className="print-toc">
          <li>Vue d'ensemble</li>
          {themesServis.map(t => <li key={t.theme.id}>{t.theme.label}</li>)}
          <li>Méthodologie et sources</li>
        </ol>
      </section>

      {/* ── Vue d'ensemble ── */}
      <section className="print-page">
        <h2 className="print-h2">Vue d'ensemble</h2>
        {/* COMMUNE DISSOUTE : la notice doit être DANS le PDF (retour codex 08/08).
            Sans elle, l'étude exportée d'une commune fusionnée n'était qu'une
            couverture suivie de pages vides — sans explication ni successeur, dans le
            document précisément fait pour circuler hors ligne, en conseil municipal.
            C'est le lecteur du PDF qui a le PLUS besoin de la notice : il n'a pas le
            site sous la main pour comprendre pourquoi la fiche est vide. */}
        {syn.commune_dissoute && (
          <div className="print-section" style={{ borderLeft: '3px solid #b45309',
                                                  paddingLeft: 12, marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>
              ⚠️ {syn.commune_dissoute.phrase}
              {syn.commune_dissoute.code_apres && (
                <> {' '}Fiche de la commune de rattachement :{' '}
                  <strong>{syn.commune_dissoute.libelle_apres}</strong>{' '}
                  ({syn.commune_dissoute.code_apres}).</>
              )}
            </p>
          </div>
        )}
        <KpiCards items={syn.tuiles} explicable={false} />
        <div className="obs2-grid">
          {/* V8 (06/08) : le profil de strate est mort — le PDF suit la vitrine. */}
          {!syn.evolution.gated && <ChartCard block={syn.evolution} color="#0047FF" niveau="h4" explicable={false} />}
        </div>
        <BandeauPerimetre hist={syn.historique_territorial} />
        {syn.synthese_llm.texte && <LlmBlock c={syn.synthese_llm} />}
      </section>

      {/* ── Thématiques ── */}
      {/* Une thématique SANS SECTION ne produit pas de page (retour codex 08/08).
          Le cas se rencontre sur une commune DISSOUTE, dont l'API rend désormais des
          thématiques vides pour garder les liens profonds vivants : sans ce filtre,
          l'étude exportée empilait onze pages blanches derrière la notice de fusion.
          Le filtre vaut au-delà de ce cas : une thématique vide n'a jamais de page. */}
      {themesServis.map(t => (
        <section className="print-page" key={t.theme.id}
                 style={{ ['--theme-color' as string]: t.theme.color }}>
          <div className="obs2-theme-head">
            <div className="obs2-theme-bar" aria-hidden="true" />
            <h2 className="obs2-theme-title">{t.theme.label}</h2>
          </div>
          {t.sections.map(s => (
            <div className="print-section" key={s.id}>
              <h3 className="obs2-section-title"><span className="num">{s.num}.</span> {s.title}</h3>
              {s.status === 'ok' ? (
                <>
                  {s.kpis && s.kpis.length > 0 && <KpiCards items={s.kpis} sources={s.sources_detail} explicable={false} />}
                  {s.charts && s.charts.length > 0 && (
                    <div className="obs2-grid">
                      {s.charts.map((ch, i) => <ChartCard key={i} block={ch} color={t.theme.color} niveau="h4"
                        sources={s.sources_detail} explicable={false} />)}
                    </div>
                  )}
                  {s.commentaire_llm?.texte && <LlmBlock c={s.commentaire_llm} />}
                </>
              ) : s.gated ? (
                // ⚠️ Pas « en cours d'ingestion » : une section HORS DROITS étiquetée comme
                // une donnée à venir est un mensonge — l'abonné restreint croirait qu'on lui
                // doit quelque chose, le prospect croirait le produit incomplet.
                <p className="print-pending">Section non incluse dans les droits de ce
                  compte — visible avec un abonnement complet.</p>
              ) : s.status === 'non_applicable' ? (
                <p className="print-pending">{s.motif || 'Section sans objet pour cette commune.'}</p>
              ) : s.id === SECTION_TOUS_EQUIPEMENTS ? (
                // equ-9 (lot I, #630) : table interactive, pas reprise dans le PDF — la phrase le
                // dit, au lieu de l'avis « aucune donnée » que le serveur sert faute de tuile.
                <p className="print-pending">{IMPRESSION_TOUS_EQUIPEMENTS}</p>
              ) : s.status === 'hors_champ_source' ? (
                // Hors du champ de la source (08/08), et depuis le 26/09 les sections du socle
                // sans donnée pour ce territoire : le motif, comme à l'écran — jamais la
                // promesse « en cours d'ingestion », fausse pour une source déjà chargée.
                <p className="print-pending">{s.motif || "Ce territoire n'entre pas dans le champ de la source de cette section."}</p>
              ) : (
                <p className="print-pending">Section en cours d'ingestion — rejoindra l'observatoire lors d'une prochaine mise à jour.</p>
              )}
            </div>
          ))}
        </section>
      ))}

      {/* ── Méthodologie ── */}
      <section className="print-page">
        <h2 className="print-h2">Méthodologie et sources</h2>
        <p className="print-methodo">
          Les indicateurs de cette étude proviennent exclusivement des sources statistiques
          officielles : INSEE (recensement, FILOSOFI, BPE), DGFiP (comptes des communes, DVF),
          DGCL (dotations), CAF, SDES (RPLS, SITADEL), DREES (RPPS, FINESS), ADEME (DPE),
          MENJ, SSMSI (délinquance enregistrée). Aucune valeur n'est estimée ni extrapolée : une donnée absente est signalée
          « N/D ». Les analyses rédigées commentent et croisent les indicateurs présentés —
          elles ne produisent jamais de chiffre qui ne figure pas dans les données. Les
          comparaisons de strate portent sur les communes de la même bande de population.
          Les valeurs historisées sont capturées mensuellement par {NOM_PRODUIT} (la profondeur
          affichée correspond à la profondeur disponible).
        </p>
        {/* A2 — la légende des comparaisons, DANS le PDF. Les tuiles ne la portent plus
            (elle y était répétée sous chacune), et le document imprimé ne peut pas
            consulter l'infobulle du marqueur « publié » : sans ce paragraphe, une étude
            exportée n'expliquerait plus d'où viennent ses valeurs d'échelle. */}
        <NoteMethodeEchelles variante="texte" />
        <p className="print-footer">{NOM_PRODUIT} — les données officielles de votre commune · {DOMAINE_PUBLIC} · {date}</p>
      </section>
    </div>
  )
}
